import { useState } from "react";
import { Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebase/firebaseConfig";
import Input from "../../components/form/input/InputField";
import Button from "../../components/ui/button/Button";
import PageMeta from "../../components/common/PageMeta";
import AuthLayout from "./AuthPageLayout";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (!email) {
      setError(t("validation.enterValidEmail"));
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage(t('auth.resetEmailSent', { email }));
    } catch (err: any) {
      switch (err?.code) {
        case "auth/invalid-email":
          setError(t("validation.emailNotValid"));
          break;
        case "auth/user-not-found":
          setError(t("validation.userNotExists"));
          break;
        case "auth/invalid-api-key":
          setError(t("validation.invalidApiKey"));
          break;
        default:
          setError(t("auth.recoveryEmailError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const ResetPasswordRightPanel = (
    <div className="relative flex items-center justify-center z-10 w-full h-full p-6 lg:p-8 xl:p-12">
      <div className="max-w-lg w-full">
        {/* Logo */}
        <div className="mb-6 text-center">
          <img
            src="/images/logo/logo-dark.svg"
            alt="Logrit.Life"
            className="h-10 lg:h-12 mx-auto drop-shadow-lg"
          />
        </div>

        {/* Welcome Back Section */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
            {t('auth.forgotPassword')} 🔐
          </h2>
          <p className="text-base lg:text-lg text-white/90 leading-relaxed">
            {t('auth.forgotPasswordDesc')}
          </p>
        </div>

        {/* Info Cards */}
        <div className="space-y-3 mb-6">
          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex-shrink-0 bg-blue-500/30 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-base">{t('auth.revisatuemail')}</h3>
                <p className="text-white/70 text-sm">{t('auth.checkEmailDesc')}</p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex-shrink-0 bg-emerald-500/30 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-base">{t('auth.procesoseguro')}</h3>
                <p className="text-white/70 text-sm">{t('auth.linkExpiresDesc')}</p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex-shrink-0 bg-purple-500/30 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold text-base">{t('auth.needHelp')}</h3>
                <p className="text-white/70 text-sm">{t('auth.contactUsDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageMeta
        title={t("common.resetPassword")}
        description={t('auth.resetyourpassword')}
      />
      <AuthLayout right={ResetPasswordRightPanel}>
        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto pt-10">
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              {t('auth.recoverPassword')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('auth.recoverPasswordDesc')}
            </p>
          </div>
          <div className="p-6 border rounded-2xl border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <form onSubmit={onSubmit}>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("auth.email")} <span className="text-error-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                    placeholder={t('auth.emailPlaceholder')}
                  />
                </div>
                {message && (
                  <div className="p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-success-800 dark:text-success-300 mb-1">{message}</p>
                        <p className="text-xs text-success-700 dark:text-success-400 mt-2">
                          💡 <strong>{t('common.tip')}:</strong> {t('auth.resetEmailTip')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {error && (
                  <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg">
                    <p className="text-sm text-error-600 dark:text-error-400">{error}</p>
                  </div>
                )}
                <div>
                  <Button className="w-full" size="sm" type="submit" disabled={loading}>
                    {loading
                      ? t("common.sending")
                      : t("auth.sendInstructions")}
                  </Button>
                </div>
              </div>
            </form>
          </div>
          
          <div className="mt-5">
            <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400">
              {t('auth.rememberedPassword')}{" "}
              <Link
                to="/signin"
                className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
              >
                {t('auth.backToSignIn')}
              </Link>
            </p>
          </div>
        </div>
      </AuthLayout>
    </>
  );
}
